import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Box, Button, CircularProgress, IconButton, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloudDownloadOutlinedIcon from '@mui/icons-material/CloudDownloadOutlined';
import dayjs from 'dayjs';
import { formatSignedAmount, isMeaningfulDifference } from '../../../Utils/annualProfitAndLossComparison';
import { PL_SOURCE, apiSourceToPlSource } from '../../../hooks/useProfitAndLossSource';

export const plSourceLabelKey = (source) => `annualReport.plSource.${source}`;
export const plSourceShortLabelKey = (source) => `annualReport.plSource.${source}Short`;

const Stat = ({ label, value, tone, selected = false }) => (
    <Box
        sx={{
            px: 1,
            py: 0.25,
            borderRadius: 1,
            backgroundColor: tone === 'diff' ? 'rgba(255, 152, 0, 0.12)' : tone === 'ok' ? 'rgba(46, 125, 50, 0.1)' : '#fff',
            border: selected ? '1px solid #304FFF' : '1px solid rgba(0,0,0,0.08)',
            display: 'flex',
            gap: 0.75,
            alignItems: 'baseline',
            whiteSpace: 'nowrap',
        }}
    >
        <Typography component="span" sx={{ fontSize: 11, color: '#666' }}>{label}</Typography>
        <Typography component="span" sx={{ fontSize: 12, fontWeight: 700, direction: 'ltr' }}>{value}</Typography>
    </Box>
);

Stat.propTypes = {
    label: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
    tone: PropTypes.oneOf(['diff', 'ok']),
    selected: PropTypes.bool,
};

/**
 * Chooses the P&L a report starts from — by sort code, detailed (ledger), or the final annual-report P&L —
 * and shows each one's net profit with the final P&L's difference.
 */
const ProfitAndLossSourceSwitch = ({
    source,
    onChange,
    allowDetailed = true,
    eligible = false,
    finalComparison = null,
    profits = {},
    loading = false,
    onReload,
    onFetchFinal,
    fetchingFinal = false,
    finalOnly = false,
    direction = 'rtl',
}) => {
    const { t } = useTranslation();
    const finalAvailable = Boolean(finalComparison);
    const canFetchFinal = Boolean(onFetchFinal) && (finalOnly || (eligible && !finalAvailable));
    const options = finalOnly
        ? [PL_SOURCE.final]
        : [PL_SOURCE.sortCode, ...(allowDetailed ? [PL_SOURCE.detailed] : []), ...(eligible ? [PL_SOURCE.final] : [])];
    const hasProfit = (key) => profits[key] !== null && profits[key] !== undefined && Number.isFinite(Number(profits[key]));

    const finalSource = finalAvailable ? apiSourceToPlSource(finalComparison.sourceType) : null;
    const comparedTo = source === PL_SOURCE.final ? finalSource : source;
    const difference = finalAvailable && hasProfit(PL_SOURCE.final) && comparedTo && hasProfit(comparedTo)
        && options.includes(comparedTo)
        ? Number(profits[PL_SOURCE.final]) - Number(profits[comparedTo])
        : null;

    return (
        <Box
            dir={direction}
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 0.75,
                p: 1,
                mb: 1,
                borderRadius: 2,
                border: '1px solid rgba(48, 79, 255, 0.25)',
                backgroundColor: 'rgba(48, 79, 255, 0.04)',
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#304FFF' }}>
                    {t('annualReport.plSource.title')}
                </Typography>
                {finalOnly ? (
                    <Typography
                        component="span"
                        sx={{
                            fontSize: 12,
                            fontWeight: 700,
                            px: 1.25,
                            py: 0.25,
                            borderRadius: 1,
                            border: '1px solid #304FFF',
                            backgroundColor: '#fff',
                        }}
                    >
                        {t('annualReport.plSource.finalOnlyLabel')}
                    </Typography>
                ) : (
                <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={source}
                    onChange={(_event, value) => value && onChange(value)}
                    sx={{ '& .MuiToggleButton-root': { textTransform: 'none', fontSize: 12, py: 0.25, px: 1.25 } }}
                >
                    {options.map((option) => {
                        const disabled = option === PL_SOURCE.final && !finalAvailable;
                        const button = (
                            <ToggleButton key={option} value={option} disabled={disabled}>
                                {t(plSourceLabelKey(option))}
                            </ToggleButton>
                        );
                        return disabled ? (
                            <Tooltip key={option} title={t('annualReport.plSource.finalNotFetched')}>
                                <span>{button}</span>
                            </Tooltip>
                        ) : button;
                    })}
                </ToggleButtonGroup>
                )}
                {canFetchFinal && (
                    <Tooltip title={t(finalAvailable ? 'annualReport.plSource.refetchFinalHint' : 'annualReport.plSource.fetchFinalHint')}>
                        <span>
                            <Button
                                size="small"
                                variant="outlined"
                                onClick={onFetchFinal}
                                disabled={loading || fetchingFinal}
                                startIcon={fetchingFinal
                                    ? <CircularProgress size={14} color="inherit" />
                                    : <CloudDownloadOutlinedIcon fontSize="small" />}
                                sx={{
                                    textTransform: 'none',
                                    fontSize: 12,
                                    fontWeight: 700,
                                    py: 0.25,
                                    gap: 0.5,
                                    color: '#FF7101',
                                    borderColor: 'rgba(255, 113, 1, 0.5)',
                                    '&:hover': { borderColor: '#FF7101', backgroundColor: 'rgba(255, 113, 1, 0.06)' },
                                }}
                            >
                                {t(finalAvailable ? 'annualReport.plSource.refetchFinal' : 'annualReport.plSource.fetchFinal')}
                            </Button>
                        </span>
                    </Tooltip>
                )}
                {onReload && (
                    <Tooltip title={t('annualReport.plSource.reload')}>
                        <span>
                            <IconButton size="small" onClick={onReload} disabled={loading}>
                                <RefreshIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                )}
            </Box>

            <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                {options.filter(hasProfit).map((option) => (
                    <Stat
                        key={option}
                        label={t(plSourceShortLabelKey(option))}
                        value={formatSignedAmount(profits[option])}
                        selected={option === source}
                    />
                ))}
                {difference !== null && (
                    <Stat
                        label={t('annualReport.plSource.differenceVsFinal', { source: t(plSourceShortLabelKey(comparedTo)) })}
                        value={formatSignedAmount(difference)}
                        tone={isMeaningfulDifference(difference) ? 'diff' : 'ok'}
                    />
                )}
            </Box>

            {finalOnly && !finalAvailable && !loading && (
                <Typography sx={{ fontSize: 11, color: '#B54708' }}>
                    {t('annualReport.plSource.finalNotFetched')}
                </Typography>
            )}
            {finalAvailable && (
                <Typography sx={{ fontSize: 11, color: '#666' }}>
                    {t('annualReport.plSource.finalHint', {
                        source: t(plSourceLabelKey(finalSource)),
                        date: finalComparison.fetchedAt ? dayjs(finalComparison.fetchedAt).format('DD/MM/YYYY HH:mm') : '—',
                    })}
                    {(finalComparison.manualLineCount > 0 || finalComparison.manuallyAddedLineCount > 0) && (
                        <> · {t('annualReport.plBase.manualHint', {
                            edited: finalComparison.manualLineCount,
                            added: finalComparison.manuallyAddedLineCount,
                        })}</>
                    )}
                </Typography>
            )}
        </Box>
    );
};

ProfitAndLossSourceSwitch.propTypes = {
    source: PropTypes.oneOf(Object.values(PL_SOURCE)).isRequired,
    onChange: PropTypes.func.isRequired,
    allowDetailed: PropTypes.bool,
    /** The case has an annual report, so the final P&L is offered (disabled until it is fetched). */
    eligible: PropTypes.bool,
    finalComparison: PropTypes.shape({
        sourceType: PropTypes.number,
        fetchedAt: PropTypes.string,
        manualLineCount: PropTypes.number,
        manuallyAddedLineCount: PropTypes.number,
    }),
    /** Net profit per source, signed like the reports; missing ones are not shown. */
    profits: PropTypes.shape({
        sortCode: PropTypes.number,
        detailed: PropTypes.number,
        final: PropTypes.number,
    }),
    loading: PropTypes.bool,
    onReload: PropTypes.func,
    /** Opens the fetch of the final P&L; offered while it was not fetched. */
    onFetchFinal: PropTypes.func,
    fetchingFinal: PropTypes.bool,
    /** Only the annual-report P&L is offered (a case with an annual report); the fetch also refetches. */
    finalOnly: PropTypes.bool,
    direction: PropTypes.oneOf(['rtl', 'ltr']),
};

export default ProfitAndLossSourceSwitch;
